import { useEffect, useRef, useState } from "react";
import { api, proxyImage } from "@/lib/api";
import { ImageOff } from "lucide-react";

// External CDN URLs (asos-media, mango, river island) are only reachable via
// backend ScraperAPI proxy. First-hit fetch can be slow (2-5s) and occasionally
// times out under load; we retry up to 2 times with backoff before showing a
// placeholder — the disk cache means subsequent tries are near-instant.
const MAX_RETRIES = 2;

export default function ProductImage({ product, className }) {
    const [blobUrl, setBlobUrl] = useState("");
    const [failed, setFailed] = useState(false);
    const [nonce, setNonce] = useState(0);
    const retriesRef = useRef(0);

    useEffect(() => {
        setFailed(false);
        setNonce(0);
        retriesRef.current = 0;
        let cancelled = false;
        let createdUrl = "";
        if (product?.image_storage_path) {
            api.get(`/files/${product.image_storage_path}`, { responseType: "blob" })
                .then((r) => {
                    if (cancelled) return;
                    createdUrl = URL.createObjectURL(r.data);
                    setBlobUrl(createdUrl);
                })
                .catch(() => setFailed(true));
        } else {
            setBlobUrl("");
        }
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [product?.image_storage_path, product?.image_url]);

    if (product?.image_storage_path && blobUrl && !failed) {
        return <img src={blobUrl} alt={product?.title || ""} className={className} loading="lazy" decoding="async" onError={() => setFailed(true)} />;
    }

    if (product?.image_url) {
        // Always render the img — let the browser handle load failures with
        // its default broken-image glyph. Better than a "failed" state that
        // sticks and hides even URLs that would succeed on retry.
        const proxied = proxyImage(product.image_url);
        return (
            <div className={`relative ${className || ""}`}>
                <img
                    src={proxied}
                    alt={product?.title || ""}
                    className={className}
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                />
                {product.image_pending ? (
                    <div className="absolute bottom-1 left-1 bg-white/85 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-semibold text-neutral-600 border border-neutral-300" title="Photo download in progress" data-testid="photo-pending-badge">
                        Photo · syncing
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <div className={`relative flex items-center justify-center bg-neutral-100 ${className || ""}`}>
            {product?.image_pending ? (
                <>
                    <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-600 animate-spin rounded-full" data-testid="photo-syncing-spinner" />
                    <div className="absolute bottom-1 left-1 right-1 text-center bg-white/85 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-semibold text-neutral-600 border border-neutral-300">
                        Photo · syncing
                    </div>
                </>
            ) : (
                <ImageOff className="h-8 w-8 text-neutral-300" strokeWidth={1.5} />
            )}
        </div>
    );
}
